import { useEffect, useMemo, useRef, useState } from 'react'
import type { PeopleCard } from '../api/people'
import { fetchPeopleFeatured, fetchPeopleFeaturedFiltered, searchPeople, searchPeopleFiltered } from '../api/people'
import type { PeopleFilters } from '../types/people'
import { useDebouncedValue } from './useDebouncedValue'

type Options = {
  sort?: 'trending' | 'newest'
  verifiedOnly?: boolean
  limit?: number
  filters?: Partial<PeopleFilters>
}

function dedupe(arr: PeopleCard[]) {
  const seen = new Map<string, PeopleCard>()
  for (const p of arr) {
    if (!p?.public_id || String(p.public_id).toLowerCase() === 'undefined') continue
    const key = p.public_id ?? `${p.handle}-${p.displayName}`
    if (key && !seen.has(String(key))) seen.set(String(key), p)
  }
  return Array.from(seen.values())
}

function filterSignature(opts?: Options) {
  const f = opts?.filters || {}
  return JSON.stringify({
    sort: opts?.sort || 'trending',
    verifiedOnly: Boolean(opts?.verifiedOnly),
    audience: f.audience || 'all',
    gender: f.gender || 'any',
    ageGroup: f.ageGroup || null,
    prosOnly: Boolean(f.prosOnly),
    prosToGo: Boolean(f.prosToGo),
    nearMe: Boolean(f.nearMe),
    zipcode: String(f.zipcode || '').trim(),
    radiusMiles: f.radiusMiles || null,
  })
}

export function usePeopleSearch(query: string, opts?: Options) {
  const [items, setItems] = useState<PeopleCard[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  const debouncedQuery = useDebouncedValue((query || '').trim(), 250)
  const signature = useMemo(() => filterSignature(opts), [opts?.sort, opts?.verifiedOnly, JSON.stringify(opts?.filters || {})])
  const baselineCacheRef = useRef<Record<string, PeopleCard[]>>({})
  const abortRef = useRef<AbortController | null>(null)
  const [lastFetchUrl, setLastFetchUrl] = useState<string>('')
  const [lastSuccessAt, setLastSuccessAt] = useState<number | null>(null)

  useEffect(() => {
    const q = debouncedQuery.trim()
    const hasQuery = q.length > 0

    if (hasQuery && items && items.length > 0 && !baselineCacheRef.current[signature]) {
      baselineCacheRef.current[signature] = items
    }
    if (!hasQuery && baselineCacheRef.current[signature]) {
      setItems(baselineCacheRef.current[signature])
    }

    if (abortRef.current) abortRef.current.abort()
    const controller = new AbortController()
    abortRef.current = controller

    setLoading(true)
    setError(null)

    ;(async () => {
      try {
        let arr: PeopleCard[]
        let urlHint = ''
        if (hasQuery) {
          const qp = new URLSearchParams({ q })
          const f = opts?.filters as any
          if (f?.sort) qp.set('sort', String(f.sort))
          if (f?.prosOnly) qp.set('pros_only', '1')
          if (f?.prosToGo) qp.set('pros_to_go', '1')
          if (f?.nearMe) qp.set('near_me', '1')
          if (f?.zipcode) qp.set('zipcode', String(f.zipcode))
          if (f?.radiusMiles) qp.set('radius_miles', String(f.radiusMiles))
          urlHint = `/api/people/search/?${qp.toString()}`
          setLastFetchUrl(urlHint)
          if (import.meta.env.DEV) console.debug('[PeopleSearch] fetch:start', { q, url: urlHint })
          arr = opts?.filters
            ? await searchPeopleFiltered(q, opts.filters as any, { signal: controller.signal })
            : await searchPeople(q, { signal: controller.signal })
        } else if (opts?.filters) {
          const qp = new URLSearchParams()
          const f = opts.filters as any
          if (f?.sort) qp.set('sort', String(f.sort))
          if (f?.prosOnly) qp.set('pros_only', '1')
          if (f?.prosToGo) qp.set('pros_to_go', '1')
          if (f?.nearMe) qp.set('near_me', '1')
          if (f?.zipcode) qp.set('zipcode', String(f.zipcode))
          if (f?.radiusMiles) qp.set('radius_miles', String(f.radiusMiles))
          qp.set('limit', String(opts?.limit ?? 24))
          urlHint = `/api/people/featured/?${qp.toString()}`
          setLastFetchUrl(urlHint)
          if (import.meta.env.DEV) console.debug('[PeopleSearch] fetch:start', { q: '', url: urlHint })
          arr = await fetchPeopleFeaturedFiltered({
            limit: opts?.limit ?? 24,
            filters: opts.filters as any,
            signal: controller.signal,
          })
        } else {
          const qp = new URLSearchParams()
          if (opts?.sort) qp.set('sort', String(opts.sort))
          qp.set('limit', String(opts?.limit ?? 24))
          urlHint = `/api/people/featured/?${qp.toString()}`
          setLastFetchUrl(urlHint)
          if (import.meta.env.DEV) console.debug('[PeopleSearch] fetch:start', { q: '', url: urlHint })
          arr = await fetchPeopleFeatured(opts?.limit ?? 24, opts?.sort, { signal: controller.signal })
        }

        if (opts?.verifiedOnly) arr = arr.filter((p) => Boolean((p as any).verified))
        arr = dedupe(arr)
        if (controller.signal.aborted) return
        setItems(arr)
        setLastSuccessAt(Date.now())
        if (import.meta.env.DEV) console.debug('[PeopleSearch] fetch:done', { q, count: arr.length, aborted: false })
        if (!hasQuery) baselineCacheRef.current[signature] = arr
      } catch (e: any) {
        if (controller.signal.aborted) return
        if (import.meta.env.DEV) console.debug('[PeopleSearch] fetch:error', { q, aborted: false, message: e?.message || String(e) })
        setError(e?.message || 'Failed to load people')
      } finally {
        if (!controller.signal.aborted) setLoading(false)
      }
    })()

    return () => controller.abort()
  }, [debouncedQuery, signature, opts?.sort, opts?.verifiedOnly, opts?.limit, JSON.stringify(opts?.filters || {})])

  return { items, error, loading, debouncedQuery, filterSignature: signature, lastFetchUrl, lastSuccessAt }
}
